"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-border pt-10">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
          [SEC_03] — TRANSMISSION SUCCESSFUL
        </p>
        <h3 className="font-default text-3xl md:text-4xl text-foreground font-bold mb-4">
          Inquiry Logged.
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Log New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              FULL NAME / CONTACT PERSON
            </FormLabel>
            <FormControl>
              <Input placeholder="CHARLES SMITH" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              CORPORATE EMAIL
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="charles@imcncdesigns.co.za" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              CONTACT NUMBER
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="082 553 8657" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              TECHNICAL SPECIFICATIONS / SPARE PARTS REQUIRED
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Please list Siemens part numbers, machine model, or specific retrofitting requirements." {...field} className="min-h-[160px] border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors resize-none rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      {submitError && <div className="border-l-2 border-destructive pl-4 py-2">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-6">
        <div className="flex items-start gap-3 max-w-sm">
          <Checkbox id="consent" className="mt-1 rounded-md border-primary" />
          <label htmlFor="consent" className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground leading-relaxed cursor-pointer">
            I authorize IMCNC Designs to process this technical inquiry. Data handled per industrial privacy standards.
          </label>
        </div>

        <Button type="submit" disabled={isSubmitting} className="font-mono text-xs uppercase tracking-[0.2em] bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8 py-6 h-auto group">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing
            </> : <>
              Submit Inquiry
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ContactInquiryForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}